import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LanguageService } from 'app/services';
import { map, Observable, switchMap } from 'rxjs';

import { API_URL_V4 } from 'src/environments/environment.local';
import * as formatDataHelpers from '../helpers/format-browse-data.helpers';
import * as filterHelpers from '../helpers/filter.helpers';
import * as sortingHelpers from '../helpers/sorting.helpers';
import { BrowseTypesEnum } from '../models/browse-types.enum';
import { BrowseFilter, FilterNamesEnum } from '../models/filter';
import { Sport } from '../models/sport';
import { BrowseSorting } from '../models/sorting';
import {
  VideoSection,
  Coaching,
  ApiHttpResponse,
} from '@kinomap-dev/ng-ui-lib';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';

@Injectable({ providedIn: 'root' })
export class SearchApi {
  private readonly contentsLimit: number = 15;
  private readonly playlistsLimit: number = 15;
  private lastSelectedSport: Sport | null = null;
  private lastSearchedTerm: string = '';
  private lastFilters: BrowseFilter[] = [];
  private lastSorting: BrowseSorting | null = null;
  private contentsParams: HttpParams = new HttpParams();
  private playlistsParams: HttpParams = new HttpParams();
  isContentsLastPageLoaded = false;
  isPlaylistsLastPageLoaded = false;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {
    this.playlistsParams = this.playlistsParams.set(
      'limit',
      this.playlistsLimit
    );
  }

  // SEARCHED CONTENTS

  getSearchedContents$(
    selectedSport: Sport = this.lastSelectedSport,
    searchedTerm: string = this.lastSearchedTerm,
    filters: BrowseFilter[] = this.lastFilters,
    sorting: BrowseSorting = this.lastSorting,
    isNextPage: boolean
  ): Observable<VideoSection[] | Coaching[]> {
    this.resetContentsParams(
      filters,
      sorting,
      selectedSport.name,
      searchedTerm
    );
    if (!isNextPage) {
      this.contentsParams = this.contentsParams.set('page', 1);
      this.isContentsLastPageLoaded = false;
    }

    const mode: string = filters.find(
      (f: BrowseFilter) => f.name === FilterNamesEnum.SearchMode
    ).selectedOptions[0].value;

    let method$: any;
    switch (mode) {
      case BrowseTypesEnum.Video:
        method$ = this.getSearchedVideos$;
        break;
      case BrowseTypesEnum.Coaching:
        method$ = this.getSearchedCoachings$;
        break;
      default:
        method$ = this.getSearchedVideos$;
        break;
    }
    method$ = method$.bind(this);

    return method$().pipe(
      map((res: VideoSection[] | Coaching[]) => {
        this.lastSelectedSport = selectedSport;
        this.lastSearchedTerm = searchedTerm;
        this.lastFilters = [
          ...filters.map((f) => {
            return { ...f };
          }),
        ];
        this.lastSorting = { ...sorting };

        if (!this.isContentsLastPageLoaded) {
          this.contentsParams = this.contentsParams.set(
            'page',
            +this.contentsParams.get('page') + 1
          );
        }

        return res;
      })
    );
  }

  private getSearchedVideos$(): Observable<VideoSection[]> {
    return this.http
      .get<
        ApiHttpResponse<VideoSection[]>
      >(`${API_URL_V4}/scenic`, { params: this.contentsParams })
      .pipe(
        map((res: ApiHttpResponse<VideoSection[]>) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;
          this.isContentsLastPageLoaded = currentPage === lastPage;
          return res.data;
        })
      );
  }

  private getSearchedCoachings$(): Observable<Coaching[]> {
    return this.http
      .get<ApiHttpResponse<Coaching[]>>(`${API_URL_V4}/coaching`, {
        params: this.contentsParams,
      })
      .pipe(
        map((res: ApiHttpResponse<Coaching[]>) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;

          this.isContentsLastPageLoaded = currentPage === lastPage;

          return res.data;
        })
      );
  }

  // SEARCHED PLAYLISTS

  getSearchedPlaylists$(
    selectedSport: Sport = this.lastSelectedSport,
    searchedTerm: string = this.lastSearchedTerm
  ): Observable<Playlist[]> {
    this.playlistsParams = this.playlistsParams
      .set('sport', selectedSport.name)
      .set('term', searchedTerm);

    if (
      !(
        selectedSport.name === this.lastSelectedSport?.name &&
        searchedTerm === this.lastSearchedTerm
      )
    ) {
      this.playlistsParams = this.playlistsParams.set('page', 1);
      this.isPlaylistsLastPageLoaded = false;
    }

    return this.http
      .get(`${API_URL_V4}/playlist`, {
        params: this.playlistsParams,
      })
      .pipe(
        map((res: any) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;

          if (currentPage === lastPage) {
            this.isPlaylistsLastPageLoaded = true;
          } else {
            this.playlistsParams = this.playlistsParams.set(
              'page',
              currentPage + 1
            );
          }

          this.lastSelectedSport = selectedSport;
          this.lastSearchedTerm = searchedTerm;

          return res.data;
        })
      );
  }

  // SEARCHED TERMS

  getFilteredMostSearchedTerms$(): Observable<string[]> {
    return this.languageService.currentLanguage$.pipe(
      switchMap(() => {
        return this.getMostSearchedTerms$();
      })
    );
  }

  private getMostSearchedTerms$(): Observable<string[]> {
    return this.http.get(`${API_URL_V4}/scenic/popular/terms`).pipe(
      map((res: ApiHttpResponse<string[]>) => {
        return res.data.sort((item1: any, item2: any) => {
          return item1.rank - item2.rank;
        });
      })
    );
  }

  // PARAMS

  private resetContentsParams(
    filters: BrowseFilter[],
    sorting: BrowseSorting,
    sportName: string,
    searchedTerm: string
  ): void {
    this.contentsParams = this.contentsParams
      .set('limit', this.contentsLimit)
      .set('sport', sportName)
      .set('term', searchedTerm)
      .set('sortDirection', filterHelpers.getSortDirectionParam(filters))
      .set('sortType', sortingHelpers.getSortTypeParam(sorting));

    const searchModeFilter: BrowseFilter = filters.find(
      (f: BrowseFilter) => f.name === FilterNamesEnum.SearchMode
    );

    switch (searchModeFilter.selectedOptions[0].value) {
      case BrowseTypesEnum.Video:
        this.setFilterVideosParams(filters);
        return;
      case BrowseTypesEnum.Coaching:
        this.setFilterCoachingsParams(filters);
        return;
      default:
        return;
    }
  }

  private setFilterVideosParams(filters: BrowseFilter[]): void {
    this.setDurationParams(filters);
    this.setDistanceParams(filters);
    this.setSlopeParams(filters);
    this.setDifficultiesParam(filters);
    this.setQualitiesParam(filters);
    this.setProgressParams(filters);
    this.setSoundParams(filters);
    this.setSpeedParams(filters);
    this.setPowerParams(filters);
    this.setTagParam(filters);
    this.setRegionParam(filters);
    this.setInAppParam(filters);
    this.setClimbCategoriesParam(filters);
    // this.setVehiclesParam(filters);
  }

  private setFilterCoachingsParams(filters: BrowseFilter[]): void {
    this.setDurationParams(filters);
    this.setDifficultiesParam(filters);
    this.setSoundParams(filters);
    this.setLangGroupParam(filters);
    this.setMachineTypeParam(filters);
  }

  private setDurationParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getDurationParams(filters);
    this.contentsParams = this.contentsParams
      .set('minDuration', min)
      .set('maxDuration', max);
  }

  private setDistanceParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getDistanceParams(filters);
    this.contentsParams = this.contentsParams
      .set('minDistance', min)
      .set('maxDistance', max);
  }

  private setSlopeParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getSlopeParams(filters);
    this.contentsParams = this.contentsParams
      .set('minPositiveSlope', min)
      .set('maxPositiveSlope', max);
  }

  private setDifficultiesParam(filters: BrowseFilter[]): void {
    const diffParam: string[] = filterHelpers.getDifficultiesParam(filters);
    if (this.contentsParams.has('difficulties[]')) {
      this.contentsParams = this.contentsParams.delete('difficulties[]');
    }
    diffParam.forEach((difficulty: string) => {
      this.contentsParams = this.contentsParams.append(
        'difficulties[]',
        difficulty
      );
    });
  }

  private setQualitiesParam(filters: BrowseFilter[]): void {
    const qualitiesParams: string[] = filterHelpers.getQualitiesParam(filters);
    if (this.contentsParams.has('qualities[]')) {
      this.contentsParams = this.contentsParams.delete('qualities[]');
    }
    qualitiesParams.forEach((quality: string) => {
      this.contentsParams = this.contentsParams.append('qualities[]', quality);
    });
  }

  private setProgressParams(filters: BrowseFilter[]): void {
    const { excludeComplete, excludeStarted } =
      filterHelpers.getProgressParam(filters);
    this.contentsParams = this.contentsParams
      .set('excludeComplete', excludeComplete)
      .set('excludeStarted', excludeStarted);
  }

  private setSoundParams(filters: BrowseFilter[]): void {
    const soundParam: string = filterHelpers.getSoundParam(filters);
    this.contentsParams = this.contentsParams.set('soundAvailable', soundParam);
  }

  private setSpeedParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getSpeedParams(filters);
    this.contentsParams = this.contentsParams
      .set('minAvgSpeed', min)
      .set('maxAvgSpeed', max);
  }

  private setPowerParams(filters: BrowseFilter[]): void {
    const [min, max]: string[] = filterHelpers.getPowerParams(filters);
    this.contentsParams = this.contentsParams
      .set('minAvgPower', min)
      .set('maxAvgPower', max);
  }

  private setTagParam(filters: BrowseFilter[]): void {
    const tagParam: number[] = filterHelpers.getTagParam(filters);

    if (this.contentsParams.has('tagIds[]')) {
      this.contentsParams = this.contentsParams.delete('tagIds[]');
    }

    tagParam.forEach((tagId: number) => {
      this.contentsParams = this.contentsParams.append('tagIds[]', tagId);
    });
  }

  private setRegionParam(filters: BrowseFilter[]): void {
    const regionParam: string[] = filterHelpers.getRegionParam(filters);
    if (regionParam.length > 0) {
      this.contentsParams = this.contentsParams.set(
        'codeCountry[]',
        regionParam.toString()
      );
    } else {
      this.contentsParams = this.contentsParams.delete('codeCountry[]');
    }
  }

  private setLangGroupParam(filters: BrowseFilter[]): void {
    const languages: string[] = filterHelpers.getLangGroupParam(filters);
    if (this.contentsParams.has('langGroup[]')) {
      this.contentsParams = this.contentsParams.delete('langGroup[]');
    }
    languages.forEach(
      (language: string) =>
        (this.contentsParams = this.contentsParams.set('langGroup[]', language))
    );
  }

  private setMachineTypeParam(filters: BrowseFilter[]): void {
    const machineTypes: string[] = filterHelpers.getMachineTypeParam(filters);
    if (this.contentsParams.has('machineTypes[]')) {
      this.contentsParams = this.contentsParams.delete('machineTypes[]');
    }
    machineTypes.forEach(
      (machineType: string) =>
        (this.contentsParams = this.contentsParams.append(
          'machineTypes[]',
          machineType
        ))
    );
  }

  private setInAppParam(filters: BrowseFilter[]): void {
    const inAppParam: string = filterHelpers.getInAppParam(filters);
    this.contentsParams = this.contentsParams.set('inApp', inAppParam);
  }

  private setClimbCategoriesParam(filters: BrowseFilter[]): void {
    const climbCategoriesParam: number[] =
      filterHelpers.getClimbCategoriesParam(filters);
    if (this.contentsParams.has('climbCategories[]')) {
      this.contentsParams = this.contentsParams.delete('climbCategories[]');
    }
    climbCategoriesParam.forEach((climbCategory: number) => {
      this.contentsParams = this.contentsParams.append(
        'climbCategories[]',
        climbCategory.toString()
      );
    });
  }

  // private setVehiclesParam(filters: BrowseFilter[]): void {
  //   const vehiclesParam: string[] = filterHelpers.getVehiclesParam(filters);
  //   if (this.contentsParams.has('vehicles[]')) {
  //     this.contentsParams = this.contentsParams.delete('vehicles[]');
  //   }
  //   vehiclesParam.forEach((vehicle: string) => {
  //     this.contentsParams = this.contentsParams.append('vehicles[]', vehicle);
  //   });
  // }
}
